//@flow
// $FlowFixMe
import type {Area} from 'react-easy-crop/types';
import {createCanvas} from '../../utils/dom/create-canvas';

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    // $FlowFixMe
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}
export type CropOptions = {
  flip?: {
    horizontal: boolean,
    vertical: boolean,
  },
  rotation?: number,
  format?: string,
  maxHeight?: number,
  maxWidth?: number,
  customBackground?: string,
  quality?: number,
};

export default async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  options: CropOptions = {}
): Promise<[File, string] | null> {
  const image = await createImage(imageSrc);

  const canvas = createCanvas(image.width, image.height);
  const ctx = canvas.getContext('2d');

  const {
    rotation = 0,
    flip = {horizontal: false, vertical: false},
    format = 'image/jpeg',
    customBackground,
    maxWidth,
    quality,
    maxHeight,
  } = options;

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  const {width: bBoxWidth, height: bBoxHeight} = rotateSize(
    image.width,
    image.height,
    rotation
  );

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  ctx.drawImage(image, 0, 0);

  let croppedWidth = pixelCrop.width;
  let croppedHeight = pixelCrop.height;

  if (maxWidth && croppedWidth > maxWidth) {
    const aspectRatio = croppedWidth / croppedHeight;
    croppedWidth = maxWidth;
    croppedHeight = croppedWidth / aspectRatio;
  }

  if (maxHeight && croppedHeight > maxHeight) {
    const aspectRatio = croppedWidth / croppedHeight;
    croppedHeight = maxHeight;
    croppedWidth = croppedHeight * aspectRatio;
  }

  const croppedCanvas = createCanvas(croppedWidth, croppedHeight);
  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    return null;
  }

  if (format.includes('jpg') || format.includes('jpeg') || customBackground) {
    croppedCtx.fillStyle = customBackground || '#fff';
    croppedCtx.fillRect(0, 0, croppedCanvas.width, croppedCanvas.height);
  }

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    croppedWidth,
    croppedHeight
  );

  const FILE_NAME = getRandomFileName();

  return new Promise((resolve, reject) => {
    if (croppedCanvas.convertToBlob) {
      const type = format === 'image/webp' ? 'image/jpeg' : format;
      croppedCanvas
        //$FlowFixMe
        .convertToBlob({type, quality})
        .then(blob => {
          if (!blob) {
            reject(new Error('Blob creation failed'));
            return;
          }
          const file = new File([blob], FILE_NAME, {type: format});
          const preview = URL.createObjectURL(blob);
          resolve([file, preview]);
        })
        .catch(error => {
          reject(error);
        });
    } else {
      croppedCanvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Blob creation failed'));
            return;
          }
          const file = new File([blob], FILE_NAME, {type: format});
          const preview = URL.createObjectURL(blob);

          resolve([file, preview]);
        },
        format,
        quality
      );
    }
  });
}

function getRandomFileName() {
  const randomString = Math.random()
    .toString(36)
    .substring(2, 8);
  const timestamp = Date.now();

  return `${randomString}-${timestamp}`;
}
